import styles from "./index.module.css";
import React, { useState, useEffect } from "react";
import { Lock } from "react-ikonate";

import { Auth } from "shared/@auth/Auth";
import Table from "shared/Table";
import Button from "shared/Button";
import Loader from "shared/Loader";
import Select from "shared/@forms/Select";
import AlertNotice from "shared/AlertNotice";
import { Section, Row, Column, LabelGroup } from "shared/layout";
import formatDate from "shared/@utils/formatDate";

import { useCreditCards } from "./useCreditCards";

export default function ({ iboNumber }) {
  const { hasPermission } = Auth.useContainer();

  const {
    creditCards,
    statusOptions,
    isWaiting,
    updateStatus,
    makePrimary,
    canChangePrimary,
  } = useCreditCards({ iboNumber });

  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Auto-select the first card on load.
  useEffect(() => {
    if (creditCards && creditCards[0] != null && selectedCard == null) {
      setSelectedCard(creditCards[0]);
      setSelectedStatus(creditCards[0].status);
    }
  }, [selectedCard, creditCards]);

  const canUpdate = hasPermission("membership.ibo.update");

  return (
    <div className={styles.container}>
      {creditCards == null || isWaiting ? (
        <Loader />
      ) : creditCards.length === 0 ? (
        <span>IBO has no credit cards on file.</span>
      ) : (
        <>
          {!canChangePrimary && (
            <AlertNotice icon={<Lock />} title="Primary Card is Locked">
              The ability to change the primary credit card is temporarily
              disabled during monthly billing. Once monthly billing is completed
              this functionality will be available.
            </AlertNotice>
          )}
          <Table maxHeight={300}>
            <thead>
              <tr>
                <th>Id</th>
                <th>Status</th>
                <th>Nickname</th>
                <th>Modified</th>
                <th>Card Number</th>
              </tr>
            </thead>
            <tbody>
              {creditCards.map((card) => (
                <tr
                  key={card.id}
                  className={
                    card.isPrimary === true
                      ? selectedCard !== null && selectedCard.id === card.id
                        ? `${styles.resultRowSelected}`
                        : `${styles.resultRowPrimary}`
                      : selectedCard !== null && selectedCard.id === card.id
                      ? `${styles.resultRowSelected}`
                      : `${styles.resultRow}`
                  }
                  onClick={() => {
                    setSelectedStatus(card.status);
                    setSelectedCard(card);
                  }}
                >
                  <td>{card.id}</td>
                  <td>
                    {card.status}{" "}
                    <span className={styles.primaryCard}>
                      {card.isPrimary ? "(Primary)" : ""}
                    </span>
                  </td>
                  <td>{card.nickname}</td>
                  <td>{formatDate(card.lastModifiedOn)}</td>
                  <td>
                    {card.ccFirstSix}******
                    {card.ccLastFour}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {selectedCard == null ? (
            <Section>
              <header>
                <span className={styles.subtitle}>
                  &nbsp; Select A Card For More Information
                </span>
              </header>
            </Section>
          ) : (
            canUpdate && (
              <div className={styles.actionButtons}>
                <Button
                  disabled={
                    !canChangePrimary ||
                    selectedCard.isPrimary ||
                    selectedCard.status !== "Active"
                  }
                  className={styles.btn}
                  onClick={() => makePrimary(selectedCard.id)}
                >
                  Make Primary
                </Button>

                <div className={styles.row}>
                  <Button
                    disabled={
                      !canChangePrimary ||
                      selectedStatus === selectedCard.status
                    }
                    className={styles.btn}
                    onClick={() =>
                      updateStatus(selectedCard.id, selectedStatus)
                    }
                  >
                    Update Status
                  </Button>

                  <div className={styles.selectContainer}>
                    <Select
                      onChange={(e) => {
                        e.preventDefault();
                        setSelectedStatus(e.target.value);
                      }}
                      value={selectedStatus}
                      disabled={!canChangePrimary}
                    >
                      {statusOptions.map((option) => {
                        return (
                          <option value={option} key={option}>
                            {option}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
              </div>
            )
          )}

          <InfoCard card={selectedCard} />
        </>
      )}
    </div>
  );
}

function InfoCard(card) {
  card = card.card;

  return (
    <div className={styles.container}>
      {card == null ? (
        <div></div>
      ) : (
        <>
          <header className={styles.detailsHeader}>
            <h3>
              Card Details{" "}
              <span className={styles.subtitle}>&mdash; {card.id}</span>
            </h3>
          </header>

          <Section>
            <Row>
              <Column>
                <LabelGroup title="Card Nickname">{card.nickname}</LabelGroup>
                <LabelGroup title="Card Holder">{card.cardholder}</LabelGroup>
              </Column>
              <Column>
                <LabelGroup title="Card Number">
                  {card.ccFirstSix}******{card.ccLastFour}
                </LabelGroup>

                <LabelGroup title="Expiration">{card.expiration}</LabelGroup>
              </Column>
              <Column>
                <LabelGroup title="Card Status">{card.status}</LabelGroup>
                <LabelGroup title="Last Modified ">
                  On: {formatDate(card.lastModifiedOn)}
                  <br />
                  By: {card.lastModifiedBy}
                </LabelGroup>
              </Column>
            </Row>
          </Section>
        </>
      )}
    </div>
  );
}
